import { Box, Button } from "@chakra-ui/react";
import { useCallback, useEffect } from "react";
import JsonRender from "../../../renderer/JsonRender";
import { useAddProgress } from "../api";
import { EventType } from "..";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
  clear,
  hideContinue,
  incrementRevealCount,
  setProgress,
  setRevealIndex,
  showContinue,
} from "../../../store/slices/contentSlice";
import { useAudio } from "../../../Providers/AudioProvider";
import { ModuleType } from "../../../internal/courses/api";
import { checkIsViewComponent } from "./Module.Content";

type IProps = {
  content: IConfig[];
  progress: IProgress;
  practiceId: string;
};

const ModulePractice = ({ content, practiceId, progress }: IProps) => {
  const { showContinueButton, revealIndex } = useAppSelector((s) => s.content);
  const { courseId = "", moduleId = "", mode = "learn" } = useParams();
  const navigate = useNavigate();
  const addProgress = useAddProgress({
    course: courseId,
    practice: practiceId,
    module: moduleId,
  });
  const { playClickSound } = useAudio();
  const dispatch = useAppDispatch();
  const currentContent = content[revealIndex];

  useEffect(() => {
    dispatch(hideContinue());
  }, []);

  useEffect(() => {
    if (!progress || progress.events.length === 0) {
      addProgress.mutate({
        event: {
          content: 0,
          isViewComponent: checkIsViewComponent(content[0].component),
          eventType: EventType.COMPLETE,
        },
      });
    } else {
      let lastOpenContentIndex = 0;
      let lastEvent = progress.events[0];

      for (let event of progress.events) {
        if (lastOpenContentIndex < event.content) {
          lastOpenContentIndex = event.content;
          lastEvent = event;
        }
      }
      dispatch(setRevealIndex(lastOpenContentIndex));
      dispatch(setProgress(lastOpenContentIndex));
      if (
        checkIsViewComponent(content[lastOpenContentIndex].component) &&
        lastEvent.isCompleted
      ) {
        dispatch(showContinue());
      } else {
        dispatch(hideContinue());
      }
    }
  }, [progress]);

  const handleLessonComplete = useCallback(() => {
    const isViewComponent = checkIsViewComponent(content[0].component);
    if (mode === "review") {
      addProgress.mutate(
        {
          event: {
            content: revealIndex,
            isViewComponent,
            eventType: EventType.REVIEW_COMPLETED,
            meta: {},
          },
        },
        {
          onSuccess: () => {
            dispatch(clear());
            navigate(`board`);
          },
        }
      );
    } else {
      addProgress.mutate(
        {
          event: {
            content: revealIndex,
            isViewComponent,
            eventType: EventType.LESSON_COMPLETE,
            meta: {
              lesson_start: true,
            },
          },
        },
        {
          onSuccess: () => {
            dispatch(clear());
            navigate(`board`);
          },
        }
      );
    }
  }, [revealIndex]);

  const handleLessonContinue = useCallback(() => {
    dispatch(hideContinue());
    addProgress.mutate({
      event: {
        content: revealIndex,
        isViewComponent: false,
        eventType: EventType.VIEW,
      },
    });
    playClickSound(0.2);
    dispatch(incrementRevealCount());
  }, [currentContent.component, revealIndex]);

  const renderButtonLearnButtons = () => {
    const isLastContent = revealIndex + 1 === content.length;

    return (
      showContinueButton && (
        <Box
          mx={"auto"}
          width={{ base: "100%", lg: "20rem" }}
          bgGradient="linear(to-t, #101010, transparent)"
          pb=".5rem"
          px="1.5rem"
          position="fixed"
          bottom={{ base: 0, md: "10rem" }}
          right="0"
          zIndex={"sticky"}
          left={0}
        >
          <Button
            variant={"secondary"}
            // isLoading={addProgress.isPending}
            onClick={
              isLastContent ? handleLessonComplete : handleLessonContinue
            }
            width={"full"}
            size={"lg"}
            zIndex={100000000}
          >
            Continue
          </Button>
        </Box>
      )
    );
  };

  return (
    <Box pb={"8rem"} position={"relative"}>
      <Box
        display={"flex"}
        justifyContent={"center"}
        alignItems={"center"}
        h={"80vh"}
      >
        <JsonRender
          content={content}
          mode="practice-view"
          practiceId={practiceId}
          lessonType={ModuleType.practice}
        />
      </Box>

      {renderButtonLearnButtons()}
    </Box>
  );
};

export default ModulePractice;
